// Metered consumption file: what to upload, a check of how the file was read
// before anything uses it, and -- once it is in -- the site's consumption per
// month, so the user sees what the study will run on.
//
// The reading itself lives in config/loadProfileImport.js, and its tests check
// every rule this screen states. Change one and the other has to follow.
import { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getRechartsTooltipProps } from "../theme";
import {
  DECIMAL_LABELS,
  DEFAULT_IMPORT_OPTIONS,
  DELIMITER_LABELS,
  MONTHS_EL_SHORT,
  MONTHS_EN_SHORT,
  UNIT_LABELS,
  buildLoadProfileTemplate,
  columnLabel,
  decodeLoadProfileBytes,
  describeReading,
  formatThousands,
  readLoadProfile,
  summarizeProfileValues,
} from "./config/loadProfileImport";
import { useLanguage } from "../context/LanguageContext";

const GUIDE = {
  el: {
    title: "Μετρημένη κατανάλωση από αρχείο",
    intro:
      "Ανεβάστε την καμπύλη φορτίου 12 συνεχόμενων μηνών, όπως τη δίνει ο ΔΕΔΔΗΕ (Πύλη Μετρητικών Δεδομένων) " +
      "ή ο πάροχος. Υπάρχει μόνο για μετρητές με ωριαία ή 15λεπτη καταγραφή. Αν ο πελάτης είναι νομικό πρόσωπο, " +
      "μπορεί από την ίδια πύλη να εξουσιοδοτήσει το γραφείο σας να δει τα δεδομένα του. Αν έχει μόνο λογαριασμούς, " +
      "επιλέξτε «Μοντελοποίηση κατανάλωσης».",
    rules: [
      "Μία γραμμή ανά ώρα ή ανά 15 λεπτά, για 12 συνεχόμενους μήνες. Οποιοδήποτε 12μηνο, π.χ. Ιούλιος 2025 – Ιούνιος 2026.",
      "Οι ημερομηνίες διαβάζονται: κάθε τιμή μπαίνει στη δική της ημέρα και ώρα. Δεκτή και μία γραμμή ανά ημέρα με 24 ή 96 τιμές.",
      "Τιμές σε kWh ανά διάστημα (ενέργεια). Αν το αρχείο έχει kW, το επιλέγετε στη «Μονάδα» μετά την εισαγωγή.",
      "Δεκαδικά με κόμμα ή τελεία, στήλες με «;», «,» ή Tab, δηλαδή όπως το αποθηκεύει το Excel.",
      "Πριν χρησιμοποιηθεί, βλέπετε πώς διαβάστηκε και την κατανάλωση ανά μήνα. Συγκρίνετέ τη με τους λογαριασμούς.",
    ],
    template:
      "Το πρότυπο ανοίγει σε στήλες στο Excel. Συμπληρώστε τη στήλη kWh και αποθηκεύστε το ως CSV.",
  },
  en: {
    title: "Measured consumption file",
    intro:
      "Upload the load curve for 12 consecutive months, as provided by HEDNO (Metering Data Portal) or the " +
      "supplier. It exists only for meters that record hourly or every 15 minutes. If the client is a company, it " +
      "can authorise your office in the same portal to see its data. If there are only bills, choose \"Model " +
      "consumption\".",
    rules: [
      "One row per hour or per 15 minutes, for 12 consecutive months. Any 12 months, e.g. July 2025 – June 2026.",
      "Dates are read: every value goes to its own day and time. One row per day with 24 or 96 values also works.",
      "Values in kWh per interval (energy). If the file holds kW, choose it under \"Unit\" after importing.",
      "Decimal comma or point; columns separated by \";\", \",\" or Tab, i.e. however Excel saves it.",
      "Before it is used you see how it was read and the consumption per month. Compare it with the bills.",
    ],
    template: "The template opens in columns in Excel. Fill in the kWh column and save it as CSV.",
  },
};
const SAMPLE = ["Ημερομηνία ώρα;kWh", "01/01/2025 00:00;41,2", "01/01/2025 01:00;39,8", "01/01/2025 02:00;38,5"];
const BILLS_MISMATCH_PCT = 25;

const readFileBytes = (file) => (typeof file.arrayBuffer === "function"
  ? file.arrayBuffer()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  }));

function MonthlyChart({ monthlyKwh, isGreek }) {
  const theme = useTheme();
  const rows = (monthlyKwh || []).map((kwh, i) => ({
    month: (isGreek ? MONTHS_EL_SHORT : MONTHS_EN_SHORT)[i],
    kwh: Math.round(kwh),
  }));
  if (!rows.length) return null;
  return (
    <Box>
      <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.5 }}>
        {isGreek ? "Κατανάλωση ανά μήνα (kWh)" : "Consumption per month (kWh)"}
      </Typography>
      <Box sx={{ height: 210 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} interval={0} />
            <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => formatThousands(v)} />
            <RechartsTooltip
              {...getRechartsTooltipProps(theme.palette.mode)}
              formatter={(value) => [`${formatThousands(value)} kWh`, isGreek ? "Κατανάλωση" : "Consumption"]}
            />
            <Bar dataKey="kwh" fill={theme.palette.primary.main} radius={[3, 3, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

function ProfileFigures({ summary, isGreek }) {
  if (!summary) return null;
  const parts = [
    `${formatThousands(summary.annualKwh)} kWh/${isGreek ? "έτος" : "year"}`,
    summary.wasAggregated
      ? (isGreek
        ? `αιχμή 15 λεπτών ${formatThousands(summary.nativePeakKw)} kW, ωριαία ${formatThousands(summary.peakKw)} kW`
        : `15-minute peak ${formatThousands(summary.nativePeakKw)} kW, hourly ${formatThousands(summary.peakKw)} kW`)
      : `${isGreek ? "αιχμή" : "peak"} ${formatThousands(summary.peakKw)} kW`,
    `${isGreek ? "συντελεστής φορτίου" : "load factor"} ${formatThousands(summary.loadFactorPct, 1)}%`,
  ];
  return (
    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
      {parts.join(" · ")}
    </Typography>
  );
}

function BillsComparison({ fileKwh, declaredAnnualKwh, isGreek }) {
  const declared = Number(declaredAnnualKwh) || 0;
  if (!(declared > 0) || !(fileKwh > 0)) {
    return (
      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1 }}>
        {isGreek
          ? "Συγκρίνετε τη μηνιαία κατανάλωση με τους λογαριασμούς του πελάτη. Μεγάλη διαφορά σημαίνει συνήθως λάθος μονάδα, δεκαδικά ή παροχή."
          : "Compare the monthly consumption with the client's bills. A large difference usually means a wrong unit, decimals or supply."}
      </Typography>
    );
  }
  const rawDiff = ((fileKwh - declared) / declared) * 100;
  // Rounded before the sign is chosen, so a match reads 0.0%, not -0.0%.
  const diffPct = Math.abs(rawDiff) < 0.05 ? 0 : rawDiff;
  const sign = diffPct > 0 ? "+" : "";
  const line = isGreek
    ? `Λογαριασμοί (από τη μοντελοποίηση): ${formatThousands(declared)} kWh/έτος · αρχείο: ${formatThousands(fileKwh)} kWh/έτος (${sign}${formatThousands(diffPct, 1)}%).`
    : `Bills (from the modelled inputs): ${formatThousands(declared)} kWh/year · file: ${formatThousands(fileKwh)} kWh/year (${sign}${formatThousands(diffPct, 1)}%).`;
  if (Math.abs(diffPct) > BILLS_MISMATCH_PCT) {
    return (
      <Alert severity="warning" sx={{ mb: 1 }}>
        <span>{line}</span>{" "}
        <span>
          {isGreek
            ? "Η διαφορά είναι μεγάλη. Ελέγξτε τη μονάδα (kWh ή kW), τα δεκαδικά και ότι πρόκειται για την ίδια παροχή."
            : "The difference is large. Check the unit (kWh or kW), the decimals and that it is the same supply."}
        </span>
      </Alert>
    );
  }
  return (
    <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1 }}>
      {line}
    </Typography>
  );
}

function MessageList({ items, severity, isGreek }) {
  if (!items?.length) return null;
  return (
    <Alert severity={severity} sx={{ mb: 1 }}>
      {items.map((m, i) => (
        <Typography key={i} variant="body2" sx={{ mt: i ? 0.5 : 0 }}>
          {isGreek ? m.el : m.en}
        </Typography>
      ))}
    </Alert>
  );
}

export default function MeteredProfileImport({
  loadProfile,
  onLoadProfileChange,
  cfg,
  declaredAnnualKwh = 0,
  showSourceNote = false,
  // One of several files (virtual net billing supplies): the guide and the
  // sample are shown once, by the project's own import, not with every file.
  compact = false,
  // The project's consumption under virtual net billing, the sum of the
  // supplies' files: shown, but changed only through the supplies.
  locked = false,
  sourceLabel = "",
}) {
  const { isGreek } = useLanguage();
  const lang = isGreek ? "el" : "en";
  const guide = GUIDE[lang];
  const fileInputRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [fileError, setFileError] = useState(null);
  const targetYear = Number(cfg?.feasibility?.simulationYear) || new Date().getFullYear() - 1;

  const profileStored = Boolean(loadProfile?.values?.length);
  const profileActive = profileStored && loadProfile?.use !== false;
  const storedSummary = useMemo(
    () => (profileStored ? summarizeProfileValues(loadProfile.values, loadProfile.resolution) : null),
    [profileStored, loadProfile]
  );

  const read = (text, options) => readLoadProfile(text, { ...options, targetYear });

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    setFileError(null);
    try {
      const bytes = new Uint8Array(await readFileBytes(file));
      const isExcelBook = (bytes[0] === 0x50 && bytes[1] === 0x4b) || (bytes[0] === 0xd0 && bytes[1] === 0xcf);
      if (isExcelBook || /\.xlsx?$/i.test(file.name)) {
        setPending(null);
        setFileError(isGreek
          ? "Αυτό είναι βιβλίο εργασίας Excel. Ανοίξτε το στο Excel, επιλέξτε «Αποθήκευση ως» → «CSV» και εισαγάγετε το αρχείο CSV."
          : "This is an Excel workbook. Open it in Excel, choose \"Save as\" → \"CSV\" and import the CSV file.");
        return;
      }
      const { text } = decodeLoadProfileBytes(bytes);
      const options = { ...DEFAULT_IMPORT_OPTIONS };
      setPending({ fileName: file.name, text, options, result: read(text, options) });
    } catch (err) {
      setPending(null);
      setFileError(`${isGreek ? "Το αρχείο δεν διαβάστηκε" : "The file could not be read"}: ${err?.message || err}`);
    }
  };

  const changeOption = (key, value) => {
    if (!pending) return;
    const options = { ...pending.options, [key]: value };
    setPending({ ...pending, options, result: read(pending.text, options) });
  };

  const accept = () => {
    const r = pending?.result;
    if (!r?.ok) return;
    const d = r.detected;
    onLoadProfileChange({
      values: r.values,
      resolution: r.resolution,
      source: pending.fileName,
      importedAt: new Date().toISOString(),
      use: true,
      import: {
        period: d.period,
        hasTimestamps: d.hasTimestamps,
        layout: d.layout,
        delimiter: d.delimiter,
        decimal: d.decimal,
        unit: d.unit,
        targetYear: d.targetYear,
      },
    });
    setPending(null);
  };

  const clearProfile = () => {
    onLoadProfileChange(null);
    setPending(null);
    setFileError(null);
  };

  const downloadTemplate = (resolution) => {
    const blob = new Blob([buildLoadProfileTemplate(resolution, targetYear)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `protypo_katanalosis_${resolution === "15min" ? "15lepta" : "oriaio"}_${targetYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const result = pending?.result;
  const d = result?.detected;
  const autoLabel = (label) => `${isGreek ? "Αυτόματο" : "Automatic"}${label ? ` (${label})` : ""}`;

  return (
    <Box>
      {!compact && !locked && (<>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
        {guide.title}
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
        {guide.intro}
      </Typography>
      {showSourceNote && (
        <Typography variant="caption" sx={{ display: "block", mb: 1, color: profileActive ? "info.main" : "text.secondary" }}>
          {profileActive
            ? (isGreek
              ? "Σε χρήση. Η μηνιαία κατανάλωση, το ωράριο και το σχήμα φορτίου παραπάνω δεν διαβάζονται όσο χρησιμοποιείται αυτό το αρχείο."
              : "In use. The monthly consumption, the workday schedule and the load shape weights above are ignored while this profile is in use.")
            : profileStored
              ? (isGreek
                ? "Αποθηκευμένο αλλά όχι επιλεγμένο. Το φορτίο μοντελοποιείται από τα στοιχεία παραπάνω."
                : "Imported but not selected. The load is modelled from the inputs above.")
              : (isGreek
                ? "Δεν έχει εισαχθεί αρχείο, οπότε το φορτίο μοντελοποιείται από τη μηνιαία κατανάλωση, το ωράριο και το σχήμα φορτίου."
                : "Not imported, so the load is modelled from the monthly consumption, the workday schedule and the load shape weights.")}
        </Typography>
      )}

      {/* The sample stays literal (data-no-translate) and is exactly what the
          template looks like once filled in Greek Excel. */}
      <Box sx={{ mb: 1, p: 1, bgcolor: "action.hover", borderRadius: 1,
        display: "grid", gridTemplateColumns: { xs: "1fr", md: "max-content 1fr" }, columnGap: 2, rowGap: 1 }}>
        <Box component="code" translate="no" data-no-translate=""
          sx={{ fontFamily: "monospace", fontSize: 11, color: "text.primary", whiteSpace: "pre", lineHeight: 1.7 }}>
          {SAMPLE.join("\n")}
        </Box>
        <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
          {guide.rules.map((rule) => (
            <Typography key={rule} component="li" variant="caption" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
              {rule}
            </Typography>
          ))}
        </Box>
      </Box>
      </>)}

      {!locked && (<>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
        <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()}>
          {profileStored
            ? (isGreek ? "Αντικατάσταση αρχείου" : "Replace file")
            : (isGreek ? "Εισαγωγή αρχείου" : "Import file")}
        </Button>
        <input ref={fileInputRef} type="file" accept=".csv,.txt,.tsv,.xlsx,.xls,text/csv,text/plain" hidden onChange={handleFile}
          data-testid="load-profile-file" />
        <Button size="small" onClick={() => downloadTemplate("hourly")}>
          {isGreek ? "Πρότυπο ωριαίο" : "Hourly template"}
        </Button>
        <Button size="small" onClick={() => downloadTemplate("15min")}>
          {isGreek ? "Πρότυπο 15 λεπτών" : "15-minute template"}
        </Button>
      </Box>
      <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
        {guide.template}
      </Typography>
      </>)}

      {fileError && <Alert severity="error" sx={{ mb: 1 }}>{fileError}</Alert>}

      {/* ----------------------- Check before use ----------------------- */}
      {pending && (
        <Box sx={{ mb: 2, p: 1.5, border: "2px solid", borderColor: result?.ok ? "primary.main" : "error.main", borderRadius: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {isGreek ? "Έλεγχος αρχείου πριν τη χρήση" : "Check the file before it is used"}: {pending.fileName}
          </Typography>
          {result?.detected?.delimiter && (
            <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
              {isGreek ? "Πώς διαβάστηκε: " : "How it was read: "}
              <b>{describeReading(result, isGreek)}</b>
            </Typography>
          )}
          <Box sx={{ display: "grid", gap: 1, mb: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" } }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="lp-delimiter">{isGreek ? "Διαχωριστικό στηλών" : "Column separator"}</InputLabel>
              <Select labelId="lp-delimiter" label={isGreek ? "Διαχωριστικό στηλών" : "Column separator"}
                value={pending.options.delimiter} onChange={(e) => changeOption("delimiter", e.target.value)}>
                <MenuItem value="auto">{autoLabel(d?.delimiter ? DELIMITER_LABELS[d.delimiter][lang] : "")}</MenuItem>
                {Object.entries(DELIMITER_LABELS).map(([value, label]) => (
                  <MenuItem key={value === "\t" ? "tab" : value} value={value}>{label[lang]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="lp-decimal">{isGreek ? "Δεκαδικά" : "Decimals"}</InputLabel>
              <Select labelId="lp-decimal" label={isGreek ? "Δεκαδικά" : "Decimals"}
                value={pending.options.decimal} onChange={(e) => changeOption("decimal", e.target.value)}>
                <MenuItem value="auto">{autoLabel(d?.decimal ? DECIMAL_LABELS[d.decimal][lang] : "")}</MenuItem>
                {Object.entries(DECIMAL_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label[lang]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="lp-unit">{isGreek ? "Μονάδα τιμών" : "Unit of the values"}</InputLabel>
              <Select labelId="lp-unit" label={isGreek ? "Μονάδα τιμών" : "Unit of the values"}
                value={pending.options.unit} onChange={(e) => changeOption("unit", e.target.value)}>
                <MenuItem value="auto">{autoLabel(d?.unit ? UNIT_LABELS[d.unit][lang] : "")}</MenuItem>
                {Object.entries(UNIT_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label[lang]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {d?.columns?.length > 1 && (
              <FormControl size="small" fullWidth>
                <InputLabel id="lp-column">{isGreek ? "Στήλη τιμών" : "Value column"}</InputLabel>
                <Select labelId="lp-column" label={isGreek ? "Στήλη τιμών" : "Value column"}
                  value={pending.options.valueColumn} onChange={(e) => changeOption("valueColumn", e.target.value)}>
                  <MenuItem value="auto">
                    {autoLabel(columnLabel(d.columns.find((c) => c.index === d.valueColumn) || d.columns[0], isGreek))}
                  </MenuItem>
                  {d.columns.map((c) => (
                    <MenuItem key={c.index} value={c.index}>{columnLabel(c, isGreek)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <MessageList items={result?.errors} severity="error" isGreek={isGreek} />
          <MessageList items={result?.warnings} severity="warning" isGreek={isGreek} />
          <MessageList items={result?.notes} severity="info" isGreek={isGreek} />

          {result?.ok && (
            <>
              <ProfileFigures summary={result} isGreek={isGreek} />
              <BillsComparison fileKwh={result.annualKwh} declaredAnnualKwh={declaredAnnualKwh} isGreek={isGreek} />
              <MonthlyChart monthlyKwh={result.monthlyKwh} isGreek={isGreek} />
            </>
          )}

          <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
            <Button variant="contained" size="small" disabled={!result?.ok} onClick={accept}>
              {isGreek ? "Χρήση αυτού του αρχείου" : "Use this file"}
            </Button>
            <Button size="small" color="inherit" onClick={() => setPending(null)}>
              {isGreek ? "Ακύρωση" : "Cancel"}
            </Button>
          </Box>
        </Box>
      )}

      {/* ----------------------- The file in use ----------------------- */}
      {profileStored && !pending && (
        <Box sx={{ mb: 2, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <Chip size="small" color={profileActive ? "success" : "default"} variant={profileActive ? "filled" : "outlined"}
              label={profileActive ? (isGreek ? "Σε χρήση" : "In use") : (isGreek ? "Αποθηκευμένο, εκτός χρήσης" : "Saved, not in use")} />
            <Typography variant="caption" sx={{ color: "text.secondary" }} data-no-translate="">
              {[
                sourceLabel || loadProfile.source,
                loadProfile.resolution === "15min"
                  ? `${formatThousands(loadProfile.values.length)} ${isGreek ? "τιμές 15 λεπτών" : "15-minute values"}`
                  : `${formatThousands(loadProfile.values.length)} ${isGreek ? "ωριαίες τιμές" : "hourly values"}`,
                loadProfile.import?.period
                  ? `${isGreek ? "μετρήσεις" : "readings"} ${loadProfile.import.period.start} – ${loadProfile.import.period.end}`
                  : null,
              ].filter(Boolean).join(" · ")}
            </Typography>
            {!locked && (<Button size="small" color="inherit" onClick={clearProfile}>
              {isGreek ? "Αφαίρεση" : "Remove"}
            </Button>)}
          </Box>
          {!loadProfile.import && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {isGreek
                ? "Το αρχείο εισήχθη με την παλαιότερη μέθοδο, που δεν διάβαζε ημερομηνίες ούτε δεκαδικό κόμμα. Ελέγξτε τη μηνιαία κατανάλωση παρακάτω· αν δεν συμφωνεί με τους λογαριασμούς, εισαγάγετε το αρχείο ξανά."
                : "This file was imported with the earlier method, which read neither dates nor decimal commas. Check the monthly consumption below; if it does not match the bills, import the file again."}
            </Alert>
          )}
          {loadProfile.import?.period && (
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1 }}>
              {isGreek
                ? `Οι τιμές έχουν τοποθετηθεί με βάση την ημερομηνία τους στο έτος της μελέτης, από Ιανουάριο έως Δεκέμβριο.`
                : `Values are placed by their date in the study year, January to December.`}
            </Typography>
          )}
          <ProfileFigures summary={storedSummary} isGreek={isGreek} />
          <BillsComparison fileKwh={storedSummary?.annualKwh} declaredAnnualKwh={declaredAnnualKwh} isGreek={isGreek} />
          <MonthlyChart monthlyKwh={storedSummary?.monthlyKwh} isGreek={isGreek} />
        </Box>
      )}
    </Box>
  );
}
